import React, { useState, useEffect } from 'react';
import MediaLibraryFilter from './components/media-library-filter/media-library-filter.tsx';
import MediaLibrarySearch from './components/media-library-search/media-library-search.tsx';
import { Button } from '@mui/material';
import {
  DeleteIcon,
  StrokeFolderIcon
} from '@shared/components/icons/svg-icon';
import { useTranslation } from 'react-i18next';
import useMediaLibraryStore from '@store/media-library-store/media-library.store';
import {
  FileTypeEnum,
  filterOptions,
  sortOptions
} from '@models/media-library.model';
import { SortOrder } from '@models/table.model';

const MediaLibraryControls: React.FC<{
  onDeleteClick: () => void;
  onMoveClick: () => void;
  allowedTypes?: (
    | FileTypeEnum.All
    | FileTypeEnum.Image
    | FileTypeEnum.Audio
    | FileTypeEnum.Video
    | FileTypeEnum.Document
  )[];
}> = ({ onDeleteClick, onMoveClick, allowedTypes }) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const {
    checkedItems,
    checkedMediaItems,
    search,
    updateSort,
    updateFilters,
    updateSearch
  } = useMediaLibraryStore();
  const hasChecked =
    Object.values(checkedItems).some(Boolean) ||
    Object.values(checkedMediaItems).some(Boolean);

  const handleFilterChange = ({
    value,
    column,
    mode
  }: {
    value: string | SortOrder;
    column: string;
    mode: 'filter' | 'sort';
  }) => {
    if (mode === 'filter') {
      updateFilters(column, value === 'all' ? [] : [value as string]);
    } else if (mode === 'sort') {
      updateSort(column, value as SortOrder);
    }
  };

  useEffect(() => {
    setSearchValue(search?.name || '');
  }, [search?.name]);

  useEffect(() => {
    const handler = setTimeout(() => {
      const trimmed = searchValue.trim();

      if (!trimmed && search?.name) {
        updateSearch('name', '');
        return;
      }

      if (trimmed && trimmed === search?.name) return;

      if (trimmed) {
        updateSearch('name', trimmed);
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [searchValue, search?.name, updateSearch]);

  return (
    <div className="flex justify-between items-center gap-4">
      <div className="flex items-center gap-4">
        <MediaLibraryFilter
          mode="sort"
          value={SortOrder.Descending}
          onFilterChange={handleFilterChange}
          dataOptions={sortOptions}
        />
        <MediaLibraryFilter
          mode="filter"
          value={
            allowedTypes && allowedTypes.length === 1
              ? allowedTypes[0].toLowerCase()
              : 'all'
          }
          onFilterChange={handleFilterChange}
          dataOptions={filterOptions}
        />
      </div>

      <div className="flex items-center gap-4">
        {hasChecked && (
          <>
            <Button
              className="h-[34px] !min-w-[100px] !px-3 text-[12px] !border-[rgba(0,82,180,1)] !text-[rgba(0,82,180,1)]"
              variant="outlined"
              onClick={onMoveClick}
              startIcon={<StrokeFolderIcon />}
            >
              {t('move')}
            </Button>

            <Button
              className="h-[34px] !min-w-[100px] !px-3 text-[12px] !border-[var(--red-3)] !text-[var(--red-3)]"
              variant="outlined"
              onClick={onDeleteClick}
              startIcon={<DeleteIcon />}
            >
              {t('delete')}
            </Button>
          </>
        )}

        <MediaLibrarySearch
          value={searchValue}
          onSearchChange={setSearchValue}
          placeholder="Search media..."
        />
      </div>
    </div>
  );
};

export default MediaLibraryControls;
